import { cn } from '@/lib/utils'

import type { AchievementTier, BadgeGlyphId } from '@scrambl/shared'
import { BADGE_GLYPHS } from '@scrambl/shared'

import { tierColors } from './badge-tier'

import './engagement.css'

export type BadgeGlyphSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE_PX: Record<BadgeGlyphSize, number> = {
  sm: 24,
  md: 40,
  lg: 64,
  xl: 112,
}

/** Ring stroke as a fraction of the 100-unit badge box. */
const RING_WIDTH: Record<BadgeGlyphSize, number> = {
  sm: 7,
  md: 5.5,
  lg: 4.5,
  xl: 3.5,
}

const RING_RADIUS = 45
const RING_LENGTH = 2 * Math.PI * RING_RADIUS

export interface BadgeGlyphProps {
  glyph: BadgeGlyphId
  tier: AchievementTier
  size?: BadgeGlyphSize
  /** Not earned yet: muted disc, dashed ring, optional progress arc. */
  locked?: boolean
  progress?: { current: number; target: number } | null
  /** Accessible name; omit for decorative use next to visible text. */
  label?: string
  /** Draw the ring in (unlock moment). */
  animateRing?: boolean
  className?: string
}

/**
 * A badge: tier-tinted disc, ring, and Scrambl's monochrome glyph from the
 * shared geometry (the same data mobile draws). Locked badges stay quiet —
 * muted disc, dashed ring, and a progress arc when there is a count to show.
 */
export function BadgeGlyph({
  glyph,
  tier,
  size = 'md',
  locked = false,
  progress,
  label,
  animateRing = false,
  className,
}: BadgeGlyphProps) {
  const px = SIZE_PX[size]
  const colors = tierColors(tier)
  const geometry = BADGE_GLYPHS[glyph] ?? BADGE_GLYPHS.medal
  const ringWidth = RING_WIDTH[size]
  const ratio =
    locked && progress && progress.target > 0
      ? Math.min(1, Math.max(0, progress.current / progress.target))
      : 0
  const glyphStroke = locked ? 'hsl(var(--muted-foreground) / 0.7)' : colors.ink
  const glyphStrokeWidth = size === 'sm' ? 2.25 : 1.75

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 100 100"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-tier={tier}
      data-locked={locked || undefined}
      className={cn('shrink-0', className)}
    >
      <circle
        cx="50"
        cy="50"
        r={RING_RADIUS}
        fill={locked ? 'hsl(var(--muted) / 0.5)' : colors.disc}
      />
      {locked ? (
        <>
          <circle
            cx="50"
            cy="50"
            r={RING_RADIUS}
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth={ringWidth}
            strokeDasharray="4 5"
          />
          {ratio > 0 && (
            <circle
              cx="50"
              cy="50"
              r={RING_RADIUS}
              fill="none"
              stroke={colors.ring}
              strokeWidth={ringWidth}
              strokeLinecap="round"
              strokeDasharray={RING_LENGTH}
              strokeDashoffset={RING_LENGTH * (1 - ratio)}
              transform="rotate(-90 50 50)"
            />
          )}
        </>
      ) : (
        <circle
          cx="50"
          cy="50"
          r={RING_RADIUS}
          fill="none"
          stroke={colors.ring}
          strokeWidth={ringWidth}
          transform="rotate(-90 50 50)"
          className={animateRing ? 'unlock-ring-draw' : undefined}
          style={
            animateRing
              ? ({ '--ring-length': RING_LENGTH } as React.CSSProperties)
              : undefined
          }
        />
      )}
      <g
        transform="translate(26 26) scale(2)"
        fill="none"
        stroke={glyphStroke}
        strokeWidth={glyphStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {geometry.paths.map(d => (
          <path key={d} d={d} />
        ))}
        {geometry.circles?.map(([cx, cy, r]) => (
          <circle key={`c${cx}-${cy}-${r}`} cx={cx} cy={cy} r={r} />
        ))}
        {geometry.dots?.map(([cx, cy, r]) => (
          <circle
            key={`d${cx}-${cy}-${r}`}
            cx={cx}
            cy={cy}
            r={r}
            fill={glyphStroke}
            stroke="none"
          />
        ))}
      </g>
    </svg>
  )
}
